import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Stack,
  TextField,
} from '@mui/material'
import React, { useState } from 'react'

// Icon imports
import DownloadIcon from '@mui/icons-material/Download'
import SendIcon from '@mui/icons-material/Send'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { getPhotoSubmissionObject } from 'core/inventory/getPhotoSubmission'
import { useLocalStorageBoolState } from 'hooks/useLocalStorage'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { usePhotosForInventoryObject } from '@/hooks/inventory/usePhotosForInventoryObject'
import { useQuery } from 'react-query'
import CarouselTab from '../tabs/CarouselTab'
import ReviewSubmittedPhotosTab from '../tabs/ReviewSubmittedPhotosTab'
import UploadPhotosTab from '../tabs/UploadPhotosTab'
import ViewPhotosTab from '../tabs/ViewPhotosTab'
import { PhotoSubmissionForeignKey, Vehicle } from '@otw/models'
import { sendMailTemplate } from '@/components/inventory/util/sendMailTemplate'
import JSZip from 'jszip'

// Email Dialog Component
interface EmailDialogProps {
  open: boolean
  onClose: () => void
  recipientEmail: string
  onSend: (email: string, message: string) => Promise<void>
  title: string
}

const EmailDialog: React.FC<EmailDialogProps> = ({ open, onClose, recipientEmail, onSend, title }) => {
  const [email, setEmail] = useState(recipientEmail)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = async () => {
    if (!email.trim()) {
      setError('Email is required')
      return
    }

    try {
      setSending(true)
      await onSend(email, message)
      onClose()
    } catch (err) {
      console.error('Error sending email:', err)
      setError('Failed to send email. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin='dense'
          label='Recipient Email'
          type='email'
          fullWidth
          value={email}
          onChange={e => setEmail(e.target.value)}
          error={!!error}
          helperText={error}
          sx={{ mb: 2 }}
        />
        <TextField
          margin='dense'
          label='Message'
          multiline
          rows={6}
          fullWidth
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder='Enter a message to include in the email...'
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='inherit' disabled={sending}>
          Cancel
        </Button>
        {/* <Button 
          onClick={handleSend} 
          color="primary" 
          variant="contained"
          startIcon={<SendIcon />}
          disabled={sending}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </Button> */}
      </DialogActions>
    </Dialog>
  )
}

interface ManagePhotosDialogProps {
  collectionName: 'master_inventory' | 'shield_inventory' | 'imported_vehicles'
  open: boolean
  onClose: () => void
  vehicle: Vehicle
}

type StagedPhoto = {
  file: File
  previewBlobUrl: string
}

const ManagePhotosDialog: React.FC<ManagePhotosDialogProps> = ({ collectionName, open, onClose, vehicle: row }) => {
  const [tabIndex, setTabIndex] = useState(0)
  const { showSuccess, showError, showInfo, showSnackbar, showWarning } = useSnackbar()
  const isMobile = useIsMobile()
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadingSelected, setIsDownloadingSelected] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [magnifierEnabled, setMagnifierEnabled] = useLocalStorageBoolState(
    'inventory-photo-magnifier-enabled',
    true,
  )

  const {
    data: photos,
    isLoading: isLoadingPhotos,
    deletePhotos,
    uploadPhotos,
    downloadAllPhotosAsZip,
    downloadSelectedPhotosAsZip,
    updatePosition,
  } = usePhotosForInventoryObject(collectionName, row.id, {
    enabled: row.photo_storage_path !== '',
  })

  const { data: photoSubmissions, isLoading: isLoadingPhotoSubmissions } = useQuery(
    ['photoSubmissions', row.id],
    async () => {
      if (!row.uploadedPhotoIds || row.uploadedPhotoIds.length === 0) return null

      const promisses = row.uploadedPhotoIds.map((photoUploadFKey: string) =>
        getPhotoSubmissionObject({ photoId: photoUploadFKey, userId: row.assigned_user?.id || '' }),
      )
      return Promise.all(promisses)
    },
    {
      enabled: !!row.uploadedPhotoIds && row.uploadedPhotoIds.length > 0,
    },
  )

  const handleUploadPhotos = async (stagedPhotos: StagedPhoto[]) => {
    try {
      await uploadPhotos(stagedPhotos)
      showSuccess('Photos and videos uploaded successfully!')
    } catch (error) {
      showError('Error uploading files')
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue)
  }

  const handleDownloadAllPhotos = async (): Promise<void> => {
    try {
      setIsDownloading(true)
      await downloadAllPhotosAsZip({
        stockNumber: row.stock_number,
        make: row.make || '',
        model: row.model || '',
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDownloadSelectedPhotos = async (selectedPhotos: any[]) => {
    try {
      setIsDownloadingSelected(true)
      await downloadSelectedPhotosAsZip(selectedPhotos, {
        stockNumber: row.stock_number,
        make: row.make || '',
        model: row.model || '',
      })
    } finally {
      setIsDownloadingSelected(false)
    }
  }

  const handleOpenEmailDialog = () => {
    setEmailDialogOpen(true)
  }

  const handleSendEmail = async (email: string, message: string) => {
    try {
      setIsSending(true)

      if (!photos || photos.length === 0) {
        throw new Error('No photos available to send')
      }

      // Generate ZIP file as blob
      const zip = new JSZip()

      showInfo('Preparing photos for email...')

      // Process each photo
      const photoPromises = photos.map(async (photo, index) => {
        try {
          // Fetch the image as a blob
          const response = await fetch(photo.url) //todo: use firebase storage
          if (!response.ok) {
            throw new Error(`Failed to fetch image ${photo.name}`)
          }

          const blob = await response.blob()

          // Create a meaningful filename
          let filename = photo.name || `photo_${index + 1}.jpg`

          // Add position to filename if available
          if (photo.position) {
            filename = `${photo.position}_${filename}`
          } else {
            filename = `photo_${index + 1}_${filename}`
          }

          // Add to zip
          zip.file(filename, blob)
          return true
        } catch (error) {
          console.error(`Error processing photo ${photo.name}:`, error)
          return false
        }
      })

      // Wait for all photos to be processed
      await Promise.all(photoPromises)

      // Generate the ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' })

      // Convert blob to base64
      const reader = new FileReader()
      const base64Promise = new Promise<string>(resolve => {
        reader.onloadend = () => {
          const base64data = reader.result as string
          resolve(base64data)
        }
      })
      reader.readAsDataURL(zipBlob)
      const base64data = await base64Promise

      showInfo('Sending email...')

      // Send email with ZIP attachment
      const success = await sendMailTemplate(email, 'vehiclePhotosEmail', {
        vehicleInfo: `${row.year} ${row.make} ${row.model} (Stock #${row.stock_number})`,
        message: message,
        zipUrl: base64data,
        photos: photos,
      })

      if (success) {
        showSuccess('Email sent successfully!')
        setEmailDialogOpen(false)
      } else {
        throw new Error('Failed to send email')
      }
    } catch (error) {
      console.error('Error sending email with photos:', error)
      showError(`Failed to send email: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsSending(false)
    }
  }

  const hasPhotos = photos && photos.length > 0

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      disableEnforceFocus
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose()
      }}
      aria-labelledby='form-dialog-title'
      fullWidth
      maxWidth='lg'
      sx={{
        justifyContent: 'center',
        '& .MuiDialog-paper': {
          minHeight: isMobile ? undefined : 640,
          ...(isMobile && { maxHeight: '100vh', display: 'flex', flexDirection: 'column' }),
        },
      }}
    >
      <DialogTitle id='form-dialog-title' sx={{ flexShrink: 0, py: isMobile ? 1.5 : 2 }}>
        {row.make} {row.model}: VIN#{row.vin}
      </DialogTitle>
      {isLoadingPhotos || isLoadingPhotoSubmissions ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: isMobile ? 0 : 500,
            minHeight: isMobile ? 280 : 450,
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: isMobile ? 1 : 2,
              flexShrink: 0,
              ...(isMobile && {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                mx: -2,
                px: 2,
              }),
            }}
          >
            <Tabs value={tabIndex} onChange={handleTabChange} centered variant={isMobile ? 'fullWidth' : 'standard'}>
              <Tab label='Carousel' />
              <Tab label={isMobile ? 'Manage' : 'Manage Photos'} />
              <Tab label={isMobile ? 'Upload' : 'Upload Photos & Videos'} />
              <Tab label={isMobile ? 'Review' : 'Review Submissions'} />
            </Tabs>
            {tabIndex === 0 && !isMobile && (
              <FormControlLabel
                control={
                  <Switch
                    checked={magnifierEnabled}
                    onChange={(_, checked) => setMagnifierEnabled(checked)}
                    size='small'
                  />
                }
                label='Image magnifier'
                sx={{ mt: 0.5, mr: 0 }}
              />
            )}
          </Box>
          <DialogContent
            sx={{
              minHeight: isMobile ? 0 : 520,
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              px: isMobile ? 2 : 3,
            }}
          >
            {tabIndex === 0 && (
              <CarouselTab photos={photos} isMobile={isMobile} magnifierEnabled={magnifierEnabled} />
            )}
            {tabIndex === 1 && (
              <ViewPhotosTab
                collectionName={collectionName}
                vehicleId={row.id}
                photos={photos}
                onDeleteSelectedPhotos={deletePhotos}
                onDownloadSelectedPhotos={handleDownloadSelectedPhotos}
                isDownloadingSelected={isDownloadingSelected}
                updatePosition={updatePosition}
                isMobile={isMobile}
              />
            )}
            {tabIndex === 2 && (
              <UploadPhotosTab onSubmitAddedPhotos={handleUploadPhotos} isMobile={isMobile} />
            )}
            {tabIndex === 3 && <ReviewSubmittedPhotosTab row={row} photoSubmissions={photoSubmissions} />}
          </DialogContent>
          <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 2, flexShrink: 0 }}>
            {hasPhotos && (
              <Stack direction='row' spacing={1}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadAllPhotos}
                  disabled={isDownloading || !hasPhotos}
                >
                  {isDownloading ? 'Downloading...' : 'Download All Photos'}
                </Button>
                {/* <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SendIcon />}
                  onClick={handleOpenEmailDialog}
                  disabled={isSending || !hasPhotos}
                >
                  {isSending ? 'Sending...' : 'Email Photos'}
                </Button> */}
              </Stack>
            )}
            <Button onClick={onClose} color='primary'>
              Close
            </Button>
          </DialogActions>
        </>
      )}

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        recipientEmail=''
        onSend={handleSendEmail}
        title='Send Vehicle Photos'
      />
    </Dialog>
  )
}

export default ManagePhotosDialog
